import React from 'react'
import { Box, Typography, Paper } from '@mui/material'
import {
  Call as CallIcon,
  Launch as UrlIcon,
  Reply as ReplyIcon
} from '@mui/icons-material'
import MessageContent from './MessageContent'
import MessageStatusIcon from './MessageStatusIcon'
import {
  WHATSAPP_MESSAGE_USER_BG,
  WHATSAPP_MESSAGE_BOT_BG,
  WHATSAPP_TEXT_COLOR,
  WHATSAPP_SECONDARY_TEXT_COLOR
} from '../../constants/whatsapp'
import { Message } from './ConversationDetailDialog'
import { formatTime } from '../../utils/dateUtils'

interface TemplateMessageBubbleProps {
  message: Message
}

const TemplateMessageBubble: React.FC<TemplateMessageBubbleProps> = ({ message }) => {
  const isOutbound = message.direction === 'outbound'
  const templateData = message.msg_template_metadata || {}
  const mediaData = message.msg_media_metadata || {}
  const components = templateData.platform_template_components || []
  
  // Find components
  const headerComponent = components.find((c: any) => c.type === 'HEADER')
  const footerComponent = components.find((c: any) => c.type === 'FOOTER')
  const buttonsComponent = components.find((c: any) => c.type === 'BUTTONS')
  
  const headerFormat = headerComponent?.format?.toUpperCase()
  const headerText = headerComponent?.text
  const footerText = footerComponent?.text
  // Use media_url from msg_media_metadata, or fallback to template example header_handle
  const mediaUrl = mediaData.media_url || headerComponent?.example?.header_handle?.[0]
  
  
  const renderHeaderMedia = () => {
    if (!mediaUrl) return null
    
    switch (headerFormat) {
      case 'IMAGE':
        return (
          <Box sx={{ position: 'relative', width: '100%', maxHeight: 300, overflow: 'hidden', borderRadius: 0.5 }}>
            <img
              src={mediaUrl}
              alt="Template header"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </Box>
        )
      
      case 'VIDEO':
        return (
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', maxHeight: 300, overflow: 'hidden', borderRadius: 0.5 }}>
            <video
              src={mediaUrl}
              controls
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )
      
      case 'DOCUMENT':
        return (
          <Box
            component="a"
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => window.open(mediaUrl, "_blank")}
            sx={{
              width: '100%',
              height: 200,
              borderRadius: 0.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              color: '#666',
              textDecoration: 'none',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              component="iframe"
              src={mediaUrl}
              sx={{
                width: '100%',
                height: '100%',
                border: 'none',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
          </Box>
        )
      
      default:
        return null
    }
  }
  
  const renderHeaderText = () => {
    if (headerFormat === 'TEXT' && headerText) {
      return (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            px: 1,
            pt: 0.5,
            color: WHATSAPP_TEXT_COLOR,
            wordBreak: 'break-word',
          }}
        >
          <MessageContent text={headerText} />
        </Typography>
      )
    }
    return null
  }
  
  const renderButtons = () => {
    if (!buttonsComponent || !buttonsComponent.buttons) return null
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)', mx: -0.5, mb: -0.5 }}>
        {buttonsComponent.buttons.map((button: any, idx: number) => {
          let ButtonIcon = ReplyIcon
          if (button.type === 'PHONE_NUMBER' || button.type === 'CALL') ButtonIcon = CallIcon
          if (button.type === 'URL') ButtonIcon = UrlIcon

          return (
            <Box
              key={idx}
              sx={{
                borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                py: 1.2,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.02)'
                }
              }}
            >
              <ButtonIcon sx={{ fontSize: 18, color: '#1b8755' }} />
              <Typography sx={{ color: '#1b8755', fontSize: '14px', fontWeight: 500 }}>
                {(button.type === 'CALL' || button.type === 'PHONE_NUMBER') && (
                  <a href={`tel:${button.phone_number}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                    {button.text || button.type}
                  </a>
                )}
                {button.type === 'URL' && (
                  <a href={button.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                    {button.text || button.type}
                  </a>
                )}
                {button.type === 'QUICK_REPLY' && button.text}
              </Typography>
            </Box>
          )
        })}
      </Box>
    )
  }
  
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOutbound ? 'flex-end' : 'flex-start',
        mb: 1,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          maxWidth: '65%',
          bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG,
          color: WHATSAPP_TEXT_COLOR,
          borderRadius: 2,
          boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
          p: 0.5,
          position: 'relative',
          '& img': { pointerEvents: 'none' },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, minWidth: 120 }}>
          {/* Header Media */}
          {renderHeaderMedia()}
          
          {/* Header Text */}
          {renderHeaderText()}
          
          <Box sx={{ px: 1, pb: 0.5, pt: 0.5 }}>
            {/* Body */}
            {message.content && (
              <Typography 
                component='div' 
                variant='body2' 
                sx={{ 
                  color: WHATSAPP_TEXT_COLOR, 
                  fontSize: '14.2px', 
                  lineHeight: '19px',
                  wordBreak: 'break-word',
                  mb: footerText ? 0.5 : 0
                }}
              >
                <MessageContent text={message.content} />
              </Typography>
            )}
            
            {/* Footer and Timestamp — Footer text is temporarily hidden product-wide */}
            {/* {footerText ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: WHATSAPP_SECONDARY_TEXT_COLOR, fontSize: '12px' }}>
                  {footerText}
                </Typography>
                <Typography 
                  variant='caption' 
                  sx={{
                    color: isOutbound ? WHATSAPP_SECONDARY_TEXT_COLOR : 'text.secondary',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    ml: 2
                  }}
                >
                  {formatTime(message.created_at)}
                  {isOutbound && <MessageStatusIcon message={message} />}
                </Typography>
              </Box>
            ) : ( */}
                <Typography 
                  variant='caption' 
                  component='div'
                  sx={{
                    color: isOutbound ? WHATSAPP_SECONDARY_TEXT_COLOR : 'text.secondary',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 0.5,
                    mt: 0.5
                  }}
                >
                  {formatTime(message.created_at)}
                  {isOutbound && <MessageStatusIcon message={message} />}
                </Typography>
            {/* )} */}
          </Box>
        </Box>
        
        {/* Buttons */}
        {renderButtons()}
      </Paper>
    </Box>
  )
}

export default TemplateMessageBubble
